import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Contact,
  Factory,
  FileText,
  MapPin,
  ScanBarcode,
  Search,
  Tag,
  UserRound,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { SearchResponseDTO, SearchResultDTO } from '@lightbm/shared';
import { useFormatters } from '@/app/app-context';
import { Spinner } from '@/components/ui/Spinner';
import { DocumentStatusBadge } from '@/features/documents/badges';
import { StockStateBadge } from '@/features/products/parts';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/cn';
import { useDebouncedValue } from '@/lib/hooks';
import { queryKeys } from '@/lib/query-client';

const RESULT_ICONS = {
  user: UserRound,
  location: MapPin,
  product: Tag,
  document: FileText,
  customer: Contact,
  supplier: Factory,
} as const;

/**
 * Global search (top bar). Keyboard and USB barcode scanner friendly: a scanner types
 * the code then Enter, like a person would. Press "/" anywhere to jump here.
 */
export function GlobalSearch({
  variant = 'bar',
  onNavigate,
}: {
  variant?: 'bar' | 'sheet';
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(variant === 'sheet');
  const [active, setActive] = useState(0);

  const query = useDebouncedValue(value.trim(), 250);
  const enabled = query.length >= 2;
  const { data, isFetching } = useQuery({
    queryKey: queryKeys.search(query),
    queryFn: ({ signal }) => api.get<SearchResponseDTO>('/search', { q: query }, signal),
    enabled,
    staleTime: 10_000,
  });
  const results = enabled ? (data?.results ?? []) : [];
  const showPanel = open && value.trim().length >= 2;

  useEffect(() => {
    if (variant !== 'bar') return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [variant]);

  const go = (result: SearchResultDTO) => {
    void navigate(result.href);
    setValue('');
    setOpen(variant === 'sheet');
    inputRef.current?.blur();
    onNavigate?.();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(index + 1, Math.max(results.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      const result = results[active];
      if (result) {
        event.preventDefault();
        go(result);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const activeId = showPanel && results[active] ? `${listId}-${active}` : undefined;

  return (
    <div className={cn('relative min-w-0', variant === 'bar' && 'w-full max-w-[720px]')}>
      <div
        className={cn(
          'flex h-12 items-center gap-3 rounded-control border border-line-strong bg-ground/60 px-4',
          'focus-within:border-primary focus-within:bg-surface focus-within:ring-3 focus-within:ring-primary/15',
        )}
      >
        <Search className="size-5 shrink-0 text-ink-2" strokeWidth={1.75} aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-label={t('search.label')}
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-activedescendant={activeId}
          aria-autocomplete="list"
          autoComplete="off"
          autoFocus={variant === 'sheet'}
          value={value}
          placeholder={t('search.placeholder')}
          onChange={(event) => {
            setValue(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(variant === 'sheet'), 150)}
          onKeyDown={onKeyDown}
          className="h-full min-w-0 flex-1 bg-transparent text-body text-ink outline-none placeholder:text-muted [&::-webkit-search-cancel-button]:hidden"
        />
        {isFetching && <Spinner className="size-4 text-muted" />}
        {variant === 'bar' && (
          <kbd className="hidden rounded-md border border-line-strong bg-surface px-2 py-0.5 font-mono text-caption text-ink-2 xl:inline">
            /
          </kbd>
        )}
        <button
          type="button"
          onClick={() => inputRef.current?.focus()}
          className="-mr-1 rounded-control p-1.5 text-ink-2 hover:bg-surface hover:text-ink"
          title={t('search.scanHint')}
          aria-label={t('search.scan')}
        >
          <ScanBarcode className="size-5" strokeWidth={1.75} />
        </button>
      </div>

      {showPanel && (
        <div
          className={cn(
            'z-40 overflow-hidden rounded-control border border-line bg-surface',
            variant === 'bar' ? 'absolute inset-x-0 top-[calc(100%+8px)] shadow-pop' : 'mt-3',
          )}
        >
          <ul
            id={listId}
            role="listbox"
            aria-label={t('search.results')}
            className="max-h-96 overflow-y-auto p-1.5"
          >
            {results.map((result, index) => {
              const Icon = RESULT_ICONS[result.type];
              return (
                <li
                  key={`${result.type}-${result.id}`}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === active}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => go(result)}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-[8px] px-3 py-2.5',
                    index === active && 'bg-ground',
                  )}
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-control bg-primary-soft text-primary">
                    <Icon className="size-[18px]" strokeWidth={1.75} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-ink">{result.title}</span>
                    {result.subtitle && (
                      <span className="block truncate font-mono text-caption text-muted">
                        {result.subtitle}
                      </span>
                    )}
                  </span>
                  <ResultMeta result={result} />
                </li>
              );
            })}
          </ul>
          {enabled && !isFetching && results.length === 0 && (
            <p className="px-4 pt-1 pb-3 text-ink-2">{t('search.noResults', { query })}</p>
          )}
          <p className="border-t border-line bg-ground/60 px-4 py-2 text-caption text-muted">
            {t('search.phaseHint')}
          </p>
        </div>
      )}
    </div>
  );
}

/** Right side of a result: stock of a product, status of a document, else its kind. */
function ResultMeta({ result }: { result: SearchResultDTO }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const meta = result.meta;
  if (result.type === 'product' && meta?.quantity !== undefined && meta.state) {
    return (
      <span className="flex shrink-0 items-center gap-2">
        <span className="tabular text-sm font-semibold whitespace-nowrap">
          {format.number(meta.quantity, meta.decimals ?? 0)}{' '}
          <span className="font-normal text-muted">{meta.unitSymbol}</span>
        </span>
        <span className="max-sm:hidden">
          <StockStateBadge state={meta.state} />
        </span>
      </span>
    );
  }
  if (result.type === 'document' && meta?.status) {
    return (
      <span className="flex shrink-0 items-center gap-2">
        <span className="text-caption text-muted max-sm:hidden">
          {t(`search.documentTypes.${meta.operationType}`)}
        </span>
        <DocumentStatusBadge type={meta.operationType} status={meta.status} />
      </span>
    );
  }
  return (
    <span className="shrink-0 text-caption text-muted">{t(`search.types.${result.type}`)}</span>
  );
}
